/**
 * Sign in (design board "01 Sign in") and the first-run welcome before it
 * (board "00 First run").
 *
 * # DECISION: both Microsoft and Google buttons run the same browser sign-in.
 * # WHY: the Word task pane can't run OAuth itself; ``signInViaBrowser`` sends
 * #      the writer to the web app's login, which offers Microsoft, Google and
 * #      password there. Two buttons match the design and what writers look
 * #      for; both land in the same, already-working flow.
 * # DATE: 2026-10-22
 *
 * # DECISION: no "Create an account" link; "Forgot password?" opens support.
 * # WHY: the add-in has no self-serve registration and the web app has no
 * #      reset page, so either link would be a dead end. Accounts are
 * #      provisioned out-of-band; support resets passwords. The design's
 * #      "New here?" line carries the terms consent instead, since signing
 * #      in is still agreeing to them.
 * # DATE: 2026-10-22
 */

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { login } from "../api/client";
import { signInViaBrowser, SsoCancelledError, SsoTimeoutError } from "../auth/browserSso";
import { brand } from "../config/brand";
import { Button, LinkButton, Mark, TextField } from "../ds/components";
import { openExternal } from "../variant-c/services/openExternal";

const REMEMBER_KEY = "asthra_remember_email";
const TNC_ACCEPTED_KEY = "asthra_tnc_accepted";
const FIRST_RUN_SEEN_KEY = "asthra_first_run_seen";

// Verified-live pages on the marketing site, listed in the AppSource
// manifest's <AppDomains>. This screen renders before login, so these links
// can't be feature-flagged: a dead one has to be fixed outright.
const SUPPORT_URL = "https://asthra-writer.ai/support";
const TRUST_URL = "https://asthra-writer.ai/trust";
const TERMS_URL = "https://asthra-writer.ai/terms";
// The web app's data-handling page: the privacy notice writers are pointed to.
const PRIVACY_URL = "https://app.sangnan.ai/web/data-handling";

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage blocked: remembering is a convenience only.
  }
}

interface Props {
  onLoginSuccess: (username: string) => void;
}

// ---------------------------------------------------------------------------
// First run (board 00)
// ---------------------------------------------------------------------------

const VALUES: ReadonlyArray<{ title: string; body: string; icon: string[] }> = [
  {
    title: "Every claim cites its source",
    body: "Click a citation to see the exact page in your protocol, SAP or TFLs.",
    icon: ["M4 2.5h6l2.5 2.5v8.5H4z", "M6.5 8h4M6.5 10.5h3"],
  },
  {
    title: "Gaps are flagged, never invented",
    body: "If your sources don't say it, the draft gets a [MISSING INFO] placeholder.",
    icon: ["M4 14V2.5", "M4 3h7l-1.5 2.5L11 8H4"],
  },
  {
    title: "You stay in control in Word",
    body: "Each section is written into your document at its heading. Edit it like any other text.",
    icon: ["M3.5 8.5l3 3 6-7"],
  },
];

/** The brand mark: Asthra's burst, or a white label's square logo. */
const BrandMark: React.FC<{ size: number }> = ({ size }) =>
  brand.key === "asthra" ? (
    <Mark style={{ display: "block", width: size, height: size }} />
  ) : (
    <img src={brand.logoMark ?? brand.logoPrimary} alt="" style={{ display: "block", height: size, width: "auto" }} />
  );

export const FirstRun: React.FC<{ onContinue: () => void }> = ({ onContinue }) => (
  <div className="as-screen as-auth">
    <div className="as-auth-hero">
      <BrandMark size={48} />
      <div className="as-auth-wordmark">{brand.name.toUpperCase()}</div>
      <h1 className="as-auth-title">Cited first drafts for regulatory writers</h1>
      <p className="as-auth-lede">
        {brand.assistantName} drafts CSRs, protocols and IBs section by section from your own source documents, here in
        Word.
      </p>
    </div>
    <div className="as-scroll">
      <div className="as-auth-values">
        {VALUES.map((v) => (
          <div key={v.title} className="as-auth-value">
            <span className="as-auth-ico">
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {v.icon.map((d) => (
                  <path key={d} d={d} />
                ))}
              </svg>
            </span>
            <div>
              <b>{v.title}</b>
              <span>{v.body}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="as-auth-cta">
      <Button variant="primary" block onClick={onContinue}>
        Get started
      </Button>
      <p className="as-auth-small">
        Already have an account?{" "}
        <LinkButton className="as-linkbtn-lg" onClick={onContinue}>
          Sign in
        </LinkButton>
      </p>
      <p className="as-auth-fine">Your documents are never used to train models.</p>
    </div>
    <div className="as-linkrow">
      <LinkButton onClick={() => openExternal(PRIVACY_URL)}>Privacy</LinkButton>
      <LinkButton onClick={() => openExternal(TERMS_URL)}>Terms</LinkButton>
      <LinkButton onClick={() => openExternal(SUPPORT_URL)}>Contact support</LinkButton>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Sign in (board 01)
// ---------------------------------------------------------------------------

export default function LoginForm({ onLoginSuccess }: Props) {
  const savedEmail = readLocal(REMEMBER_KEY) || "";
  const [showFirstRun, setShowFirstRun] = useState(() => readLocal(FIRST_RUN_SEEN_KEY) !== "true" && !savedEmail);
  const [username, setUsername] = useState(savedEmail);
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(!!savedEmail);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rateLimitSecondsLeft, setRateLimitSecondsLeft] = useState<number | null>(null);
  const [browserBusy, setBrowserBusy] = useState(false);
  const browserAbort = useRef<AbortController | null>(null);

  const busy = loading || browserBusy;
  const rateLimited = rateLimitSecondsLeft !== null && rateLimitSecondsLeft > 0;

  useEffect(() => {
    if (rateLimitSecondsLeft === null || rateLimitSecondsLeft <= 0) return;
    const id = setTimeout(() => setRateLimitSecondsLeft((prev) => (prev !== null ? prev - 1 : null)), 1000);
    return () => clearTimeout(id);
  }, [rateLimitSecondsLeft]);

  const finish = (user: string) => {
    writeLocal(REMEMBER_KEY, rememberMe ? user : null);
    writeLocal(TNC_ACCEPTED_KEY, "true");
    writeLocal(FIRST_RUN_SEEN_KEY, "true");
    onLoginSuccess(user);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) return;
    setLoading(true);
    setError(null);
    setRateLimitSecondsLeft(null);
    try {
      const resp = await login(username, password);
      finish(resp.username);
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      const msg = err instanceof Error ? err.message : "Sign-in failed";
      if (status === 429) {
        setRateLimitSecondsLeft(60);
      } else if (msg.includes("401") || status === 401) {
        setError("That email and password don't match an account.");
      } else if (msg.includes("Failed to fetch") || msg.includes("NetworkError")) {
        setError("Can't reach the server. Check your connection and try again.");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBrowserSignIn = async () => {
    if (busy || rateLimited) return;
    setError(null);
    setRateLimitSecondsLeft(null);
    const controller = new AbortController();
    browserAbort.current = controller;
    setBrowserBusy(true);
    try {
      const { username: user } = await signInViaBrowser(controller.signal);
      finish(user);
    } catch (err: unknown) {
      if (err instanceof SsoCancelledError) return;
      if (err instanceof SsoTimeoutError) {
        setError(err.message);
        return;
      }
      const msg = err instanceof Error ? err.message : "Sign-in failed";
      setError(
        msg.includes("Failed to fetch") || msg.includes("NetworkError")
          ? "Can't reach the server. Check your connection and try again."
          : msg,
      );
    } finally {
      setBrowserBusy(false);
      browserAbort.current = null;
    }
  };

  if (showFirstRun) {
    return (
      <FirstRun
        onContinue={() => {
          writeLocal(FIRST_RUN_SEEN_KEY, "true");
          setShowFirstRun(false);
        }}
      />
    );
  }

  return (
    <div className="as-screen as-auth">
      <div className="as-auth-hero as-auth-hero-signin">
        <BrandMark size={56} />
        <div className="as-auth-wordmark as-auth-wordmark-lg">{brand.name.toUpperCase()}</div>
        <p className="as-auth-tagline">Your sources in. A cited, submission-ready draft out.</p>
      </div>

      <div className="as-scroll">
        <div className="as-auth-form">
          {browserBusy ? (
            <div className="as-auth-waiting" role="status">
              <span className="as-spin" aria-hidden="true" />
              <span>Finish signing in in the browser window that just opened, then come back here.</span>
              <Button variant="secondary" size="sm" onClick={() => browserAbort.current?.abort()}>
                Cancel
              </Button>
            </div>
          ) : (
            <>
              <Button variant="primary" block disabled={busy || rateLimited} onClick={handleBrowserSignIn}>
                Sign in with Microsoft
              </Button>
              <Button variant="secondary" block disabled={busy || rateLimited} onClick={handleBrowserSignIn}>
                Sign in with Google
              </Button>
            </>
          )}
          <div className="as-rule">or use your work email</div>
          <form className="as-auth-email" onSubmit={handleSubmit}>
            <TextField
              id="email"
              label="Work email"
              type="email"
              autoComplete="username"
              value={username}
              onChange={setUsername}
              disabled={loading}
            />
            <TextField
              id="password"
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
              disabled={loading}
            />
            <div className="as-between as-auth-row">
              <label className="as-checkline as-auth-keep">
                <input
                  type="checkbox"
                  className="as-check-input"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Keep me signed in
              </label>
              <LinkButton onClick={() => openExternal(SUPPORT_URL)}>Forgot password?</LinkButton>
            </div>
            {rateLimitSecondsLeft !== null ? (
              <p className="as-auth-error" role="alert">
                {rateLimitSecondsLeft > 0
                  ? `Too many attempts. Try again in ${rateLimitSecondsLeft} seconds.`
                  : "You can try signing in again."}
              </p>
            ) : error ? (
              <p className="as-auth-error" role="alert">
                {error}
              </p>
            ) : null}
            <Button type="submit" variant="secondary" block disabled={busy || !username || !password || rateLimited}>
              {loading ? <span className="as-spin" aria-label="Signing in" /> : "Sign in with email"}
            </Button>
          </form>
          <p className="as-auth-small">
            By signing in you agree to the{" "}
            <LinkButton className="as-linkbtn-lg" onClick={() => openExternal(TERMS_URL)}>
              Terms
            </LinkButton>
            .
          </p>
        </div>
      </div>

      <div className="as-linkrow">
        <LinkButton onClick={() => setShowFirstRun(true)}>What is {brand.assistantName}?</LinkButton>
        <LinkButton onClick={() => openExternal(PRIVACY_URL)}>Privacy</LinkButton>
        <LinkButton onClick={() => openExternal(TERMS_URL)}>Terms</LinkButton>
        <LinkButton onClick={() => openExternal(TRUST_URL)}>Trust Center</LinkButton>
        <LinkButton onClick={() => openExternal(SUPPORT_URL)}>Contact support</LinkButton>
      </div>
    </div>
  );
}
